import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import { updateCollection } from '@platform/client';
import { COLLECTION_NOUN } from '@platform/schema/content';
import { useQueryClient } from '@tanstack/react-query';

import { useTrackedMutation } from '../actions/ActionsProvider';
import { PageHeader } from '../routes/PageHeader';

import { collectionChanged } from './collection';
import { STATUS_COLOUR } from './status';

import type { Collection } from './collection';

/**
 * The top of every collection detail screen — GEN-59.
 *
 * `CourseDetail` and `PathDetail` differ in what they show *underneath*: a course lists
 * the modules below it, a path is a sequence you drag. Everything above that is the same
 * question asked of the same row, and GEN-60 will ask it a third time.
 *
 * `library-screens.md` already argues this for the list screens — four screens invented
 * separately are four different screens, and the differences are all accidental. It is
 * the same argument one level down, and there was already a difference to show for it:
 * a course could be archived and a path could not, for no reason anybody wrote down.
 */
export function CollectionHeader({ row }: { row: Collection }) {
  const queries = useQueryClient();
  const noun = COLLECTION_NOUN[row.kind];

  const archive = useTrackedMutation(`Archive the ${noun}`, {
    mutationFn: async () => {
      await updateCollection({
        path: { id: row.id },
        body: { status: 'ARCHIVED' },
        throwOnError: true,
      });
    },
    onSuccess: () => collectionChanged(queries, row.id),
  });

  return (
    <Stack spacing={2}>
      <PageHeader
        title={row.title}
        action={
          <Chip
            size="small"
            color={STATUS_COLOUR[row.status]}
            label={row.status}
          />
        }
      />

      {/* Archived rather than deleted. Published content that vanishes takes a learner's
          progress with it, and `ContentStatus` has the value for exactly this. */}
      {row.status !== 'ARCHIVED' && (
        <Stack direction="row">
          <Button
            size="small"
            color="error"
            disabled={archive.isPending}
            onClick={() => {
              archive.mutate();
            }}
          >
            Archive this {noun}
          </Button>
        </Stack>
      )}
    </Stack>
  );
}
